'use client';

import { faUserDoctor } from '@fortawesome/pro-light-svg-icons';
import { useState } from 'react';
import {
  Partner,
  PartnerProfessionalResults,
} from '@medprev-web-app/api-graphql-client';

import { getPartnerProfessionals } from '@/app/(main)/(pages)/(institucional)/(index)/parceiros/_services/get-partner-professionals.service';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { Button } from '@/components/ui/button/button.component';
import { Link } from '@/components/ui/link/link.component';
import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

type PartnerProfessionalsProps = {
  initialFetchResponse: PartnerProfessionalResults;
  partner?: Partner | null;
};

const getFirstTwoSpecialties = (specialties: string[]) => {
  if (!specialties || specialties.length === 0) {
    return '';
  }

  const firstTwo = specialties.slice(0, 2);
  const formattedString = firstTwo.join(' e ');
  return firstTwo.length === 2 ? formattedString : firstTwo[0];
};

export function PartnerProfessionals({
  partner,
  initialFetchResponse,
}: PartnerProfessionalsProps) {
  const { total: totalProfessionals } = initialFetchResponse;
  const [professionals, setProfessionals] = useState<
    typeof initialFetchResponse.professionals
  >([]);
  const [page, setPage] = useState(initialFetchResponse.pagination.page);
  const [loading, setLoading] = useState(false);

  const loadMore = async () => {
    setLoading(true);
    const response = await getPartnerProfessionals(
      partner?.id as string,
      page + 1,
    );
    if (response) {
      setProfessionals([...professionals, ...(response?.professionals ?? [])]);
      setPage(page + 1);
      setLoading(false);
    }
  };

  const canLoadMore = page * 5 < initialFetchResponse.total;

  const partnerLabel = () => {
    if (partner?.socialName) {
      return formatToTitleCase(partner?.socialName);
    }

    return formatToTitleCase(partner?.legalName as string);
  };

  return (
    <section>
      <p className="text-gray-500 leading-6 mb-2">
        {totalProfessionals === 1
          ? `${totalProfessionals} profissional atende em ${partnerLabel()}`
          : `${totalProfessionals} profissionais atendem em ${partnerLabel()}`}
      </p>

      <ul>
        {initialFetchResponse.professionals.map((professional) => {
          const specialtiesToDisplay = getFirstTwoSpecialties(
            professional.specialties as string[],
          );
          return (
            <li
              key={professional.id}
              className="py-2 flex flex-row gap-4 items-center"
            >
              <div className="self-start">
                <Link
                  prefetch={false}
                  href={`/profissionais/${professional.slug}/`}
                  data-analytics-event={AnalyticsEnum.CLICK_PARTNER_OTHER_PARTNER}
                  data-analytics-properties={JSON.stringify({ profissional_nome: professional.professionalName })}
                >
                  <EntityAvatar
                    src={professional.avatarSmall as string}
                    alt={`Foto de perfil de ${professional.professionalName}`}
                    className="max-w-[4.5rem] max-h-[4.5rem]"
                    width={64}
                    height={64}
                    shouldAddLightbox={false}
                    size="md"
                    type={EntityTypeEnum.PROFESSIONAL}
                  />
                </Link>
              </div>
              <div className="flex flex-col items-start justify-center">
                <Link
                  prefetch={false}
                  href={`/profissionais/${professional.slug}/`}
                  className="text-green-500 text-lg font-bold leading-6 hover:underline"
                  data-analytics-event={AnalyticsEnum.CLICK_PARTNER_OTHER_PARTNER}
                  data-analytics-properties={JSON.stringify({ profissional_nome: professional.professionalName })}
                >
                  {professional.professionalName}
                </Link>
                <span className="text-gray-500 text-sm font-light leading-5">
                  {specialtiesToDisplay}
                </span>
              </div>
            </li>
          );
        })}
        {professionals.map((professional) => {
          const specialtiesToDisplay = getFirstTwoSpecialties(
            professional.specialties as string[],
          );
          return (
            <li
              key={professional.id}
              className="py-2 flex flex-row gap-4 items-center"
            >
              <div className="self-start">
                <Link prefetch={false} href={`/profissionais/${professional.slug}/`}>
                  <EntityAvatar
                    src={professional.avatarSmall as string}
                    alt={`Foto de perfil de ${professional.professionalName}`}
                    type={EntityTypeEnum.PROFESSIONAL}
                    className="max-w-[4.5rem] max-h-[4.5rem]"
                    width={64}
                    height={64}
                    shouldAddLightbox={false}
                  />
                </Link>
              </div>
              <div className="flex flex-col items-start justify-center">
                <Link
                  prefetch={false}
                  href={`/profissionais/${professional.slug}/`}
                  className="text-green-500 text-lg font-bold leading-6 hover:underline"
                  data-analytics-event={AnalyticsEnum.CLICK_PARTNER_OTHER_PARTNER}
                  data-analytics-properties={JSON.stringify({
                    profissional_nome: professional.professionalName,
                  })}
                >
                  {professional.professionalName}
                </Link>
                <span className="text-gray-500 text-sm font-light leading-5">
                  {specialtiesToDisplay}
                </span>
              </div>
            </li>
          );
        })}
        {canLoadMore && (
          <Button
            isLoading={loading}
            className="w-full py-2 mt-3"
            variant="secondary"
            startIcon={faUserDoctor}
            onClick={loadMore}
            disabled={loading}
            data-analytics-event={AnalyticsEnum.CLICK_PARTNER_SHOW_MORE_PROFESSIONAL}
          >
            Mostrar mais profissionais
          </Button>
        )}
      </ul>
    </section>
  );
}
